import { useCallback, useMemo } from 'react';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';

import { TClip } from '../types';

type TUseBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState = {
  data: TClip[];
};
export function useClipNavigation({
  data: items,
}: TUseBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState) {
  const { activeClip, setActiveClip, setVisibleBox } = useBroadcastScheduleProgram();

  const handleNextItem = useCallback(() => {
    if (!activeClip) return;

    const currentIndex = items.findIndex(item => item.id === activeClip.id);

    if (currentIndex === -1 || currentIndex === items.length - 1) return;

    const nextItem = items[currentIndex + 1];

    if (nextItem) {
      setActiveClip(prev => {
        if (prev?.id) {
          setVisibleBox(null);
        }
        return nextItem;
      });
    }
  }, [activeClip, items, setActiveClip]);

  const hasNextItem = useMemo(() => {
    if (!activeClip) return false;
    return items.findIndex(item => item.id === activeClip.id) !== items.length - 1;
  }, [activeClip, items]);

  const handlePreviousItem = useCallback(() => {
    if (!activeClip) return;

    const currentIndex = items.findIndex(item => item.id === activeClip.id);

    if (currentIndex === -1 || currentIndex === 0) return;

    const nextItem = items[currentIndex - 1];

    if (nextItem) {
      setActiveClip(prev => {
        if (prev?.id) {
          setVisibleBox(null);
        }
        return nextItem;
      });
    }
  }, [activeClip, items, setActiveClip]);

  const hasPreviousItem = useMemo(() => {
    if (!activeClip) return false;
    return items.findIndex(item => item.id === activeClip.id) > 0;
  }, [activeClip, items]);

  const handleGoXItem = useCallback(
    (index: number) => {
      if (items[index]) {
        setActiveClip(prev => {
          if (prev?.id) {
            setVisibleBox(null);
          }
          return items[index];
        });
      }
    },
    [items, setActiveClip]
  );

  const handleGoLast = useCallback(() => {
    if (items[items.length - 1]) {
      setActiveClip(prev => {
        if (prev?.id) {
          setVisibleBox(null);
        }
        return items[items.length - 1];
      });
    }
  }, [items, setActiveClip]);

  const handleGoFirst = useCallback(() => {
    if (items[0]) {
      setActiveClip(prev => {
        if (prev?.id) {
          setVisibleBox(null);
        }
        return items[0];
      });
    }
  }, [items, setActiveClip]);
  return {
    handleNextItem,
    hasNextItem,
    handlePreviousItem,
    hasPreviousItem,
    handleGoXItem,
    handleGoLast,
    handleGoFirst,
  };
}
